import { FC } from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";
import { CaptionStyle } from "./types";
import { HighlightedCaption } from "./HighlightedCaption";
import { WordByWordCaption } from "./WordByWordCaption";
import { CaptionLine as CaptionLineModel } from "../models";
import { SlideshowPropsSchema } from "../schemas";
import { z } from "zod";

type CaptionDisplayProps = {
  captionLines: CaptionLineModel[];
  captionStyles: z.infer<typeof SlideshowPropsSchema>["captionStyles"];
};

export const CaptionDisplay: FC<CaptionDisplayProps> = ({
  captionLines,
  captionStyles,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const currentTimeSeconds = frame / fps;

  const currentLine = captionLines.find(
    (line) => currentTimeSeconds >= line.start && currentTimeSeconds <= line.end
  );

  const currentWord = currentLine?.words.find(
    (word) => currentTimeSeconds >= word.start && currentTimeSeconds <= word.end
  );

  if (!currentLine) return null;

  const CaptionComponent =
    captionStyles.style === CaptionStyle.Highlighted
      ? HighlightedCaption
      : WordByWordCaption;

  return (
    <div
      style={{
        fontSize: captionStyles.fontSize,
        fontFamily: captionStyles.fontFamily,
        fontWeight: captionStyles.fontWeight,
        bottom: `${100 - captionStyles.position}%`,
        position: "absolute",
        width: "100%",
      }}
    >
      <p
        style={{
          textAlign: "center",
          color: captionStyles.color,
          WebkitTextStroke: `${captionStyles.strokeWidth}px ${captionStyles.strokeColor}`,
        }}
      >
        {currentLine.words.map((word, index) => (
          <CaptionComponent
            key={`${word.word}-${index}`}
            text={word.word}
            isHighlighted={word === currentWord}
            highlightColor={captionStyles.highlightColor}
            currentTime={currentTimeSeconds}
            wordStart={word.start}
          />
        ))}
      </p>
    </div>
  );
};
